import { useContext, useEffect, useMemo, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useSearchParams } from "react-router-dom";
import {
  publicLegalShellStyle,
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionHeadingStyle,
  renderPublicLegalHeader,
} from "../../../components/general/publicPageStyles";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { useResponsive } from "../../../responsive/ResponsiveProvider";

const RELEASE_NOTES_TIMEZONE = "America/Los_Angeles";

type ReleaseNoteSection = {
  title: string;
  items: string[];
};

type ReleaseNoteItem = {
  id: string;
  slug: string;
  releaseDate: string;
  versionLabel: string;
  title: string;
  summary: string;
  tags: string[];
  sections: ReleaseNoteSection[];
};

const formatReleaseDate = (value?: string | null) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "Date not set";
  }

  const parsed = new Date(`${normalized}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return new Intl.DateTimeFormat("en-US", {
    timeZone: RELEASE_NOTES_TIMEZONE,
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const filterPanelStyle: React.CSSProperties = {
  background: "linear-gradient(180deg, #fffaf4 0%, #ffffff 100%)",
  border: "1px solid #f2ddc7",
  borderRadius: "18px",
  padding: "1.25rem",
  marginBottom: "1.5rem",
};

const releaseCardStyle: React.CSSProperties = {
  border: "1px solid #d9e8f5",
  borderRadius: "18px",
  padding: "1.25rem",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  marginTop: "1.25rem",
};

const sectionHeadingStyle: React.CSSProperties = publicSectionHeadingStyle;

const ReleaseNotes = () => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState<ReleaseNoteItem[]>([]);
  const [timezoneLabel, setTimezoneLabel] = useState(RELEASE_NOTES_TIMEZONE);
  const [query, setQuery] = useState(() => String(searchParams.get("q") || ""));
  const [activeReleaseSlug, setActiveReleaseSlug] = useState("");
  const mobile = useResponsive().isCompact;

  useEffect(() => {
    setQuery(String(searchParams.get("q") || ""));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const loadReleaseNotes = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await backendAPIStore.getPublicReleaseNotes({
          q: String(searchParams.get("q") || "").trim(),
        });

        if (cancelled) {
          return;
        }

        setItems(Array.isArray(response?.items) ? response.items : []);
        setTimezoneLabel(String(response?.timezone || RELEASE_NOTES_TIMEZONE));
      } catch (loadError: any) {
        if (cancelled) {
          return;
        }

        setItems([]);
        setError(loadError?.message || "Unable to load release notes.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadReleaseNotes();

    return () => {
      cancelled = true;
    };
  }, [backendAPIStore, searchParams]);

  useEffect(() => {
    if (!items.length) {
      return;
    }

    const anchorId = window.location.hash.replace(/^#/, "").trim();
    if (anchorId) {
      setActiveReleaseSlug(anchorId);
      const target = document.getElementById(anchorId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    if (!activeReleaseSlug) {
      setActiveReleaseSlug(items[0]?.slug || "");
    }
  }, [activeReleaseSlug, items]);

  const activeFilterSummary = useMemo(() => {
    const segments: string[] = [];
    if (String(searchParams.get("q") || "").trim()) {
      segments.push(`Text: ${String(searchParams.get("q") || "").trim()}`);
    }
    return segments;
  }, [searchParams]);

  const applyFilters = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const normalizedQuery = String(query || "").trim();

    if (normalizedQuery) {
      nextSearchParams.set("q", normalizedQuery);
    } else {
      nextSearchParams.delete("q");
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const clearFilters = () => {
    setQuery("");
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const copyReleaseLink = async (slug: string) => {
    const nextUrl = `${window.location.origin}${window.location.pathname}#${slug}`;
    try {
      await navigator.clipboard.writeText(nextUrl);
    } catch (error) {
      window.location.hash = slug;
    }
  };

  const scrollToRelease = (slug: string) => {
    setActiveReleaseSlug(slug);
    window.location.hash = slug;
    const target = document.getElementById(slug);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const filteredItems = useMemo(() => items, [items]);

  return (
    <div style={publicPageContainerStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          className="prose"
          style={{
            ...publicLegalShellStyle,
            fontFamily: "Georgia, 'Times New Roman', serif",
            maxWidth: mobile ? "100%" : "980px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          {renderPublicLegalHeader(
            "Release Notes",
            <><strong>{authExperience.brandName}</strong> platform updates</>,
            <><strong>Updated:</strong> September 9, 2026 &nbsp;|&nbsp; <strong>Timezone:</strong> {timezoneLabel}</>,
          )}

          <p style={{ fontStyle: "italic", marginBottom: "1.5rem" }}>
            This page shares product updates so providers, caregivers, nurses, and staff can quickly understand what changed and how it may affect daily work. You can search by keyword and use the release timeline to jump to a specific update.
          </p>

          <div style={filterPanelStyle}>
            <div className="grid align-items-end">
              <div className="col-12 md:col-9">
                <label htmlFor="releaseNotesSearch" className="block mb-2" style={{ fontWeight: 700, color: "#0f3d63" }}>
                  Search release notes
                </label>
                <InputText
                  id="releaseNotesSearch"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      applyFilters();
                    }
                  }}
                  placeholder="Search eMAR, daily notes, billing, reports..."
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-12 md:col-3">
                <div className="flex gap-2 w-full">
                  <Button label="Apply" icon="pi pi-filter" outlined onClick={applyFilters} style={{ width: "100%" }} />
                  <Button label="Clear" icon="pi pi-times" outlined severity="secondary" onClick={clearFilters} style={{ width: "100%" }} />
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap align-items-center gap-2">
              <Tag value={`${items.length} release${items.length === 1 ? "" : "s"}`} severity="info" />
              {activeFilterSummary.map((segment) => (
                <Tag key={segment} value={segment} severity="success" />
              ))}
            </div>
          </div>

          <h3 style={sectionHeadingStyle}>Release History</h3>

          {loading ? (
            <div className="flex flex-column align-items-center justify-content-center" style={{ minHeight: "220px", gap: "1rem" }}>
              <ProgressSpinner style={{ width: "56px", height: "56px" }} strokeWidth="4" />
              <div style={publicMutedTextStyle}>Loading release notes...</div>
            </div>
          ) : error ? (
            <div style={{ color: "#b91c1c", fontWeight: 700 }}>{error}</div>
          ) : items.length === 0 ? (
            <div>
              <div style={{ color: "#0f3d63", fontWeight: 700, fontSize: "1.1rem" }}>No release notes matched these filters</div>
              <p style={{ ...publicMutedTextStyle, margin: "0.75rem 0 0" }}>
                Try clearing the text search or using a broader keyword.
              </p>
            </div>
          ) : (
            <div>
              {filteredItems.map((item) => (
                <section key={item.id} id={item.slug} style={releaseCardStyle}>
                  <div className="flex flex-wrap align-items-start justify-content-between gap-3">
                    <div style={{ minWidth: 0, flex: "1 1 620px" }}>
                      <div className="flex flex-wrap align-items-center gap-2 mb-2">
                        <Tag value={item.versionLabel} severity="contrast" />
                        <Tag value={formatReleaseDate(item.releaseDate)} severity="info" />
                      </div>
                      <h3 style={{ color: "#0f3d63", fontSize: "1.35rem", margin: "0 0 0.5rem" }}>{item.title}</h3>
                      <p style={{ ...publicMutedTextStyle, margin: 0 }}>{item.summary}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={`#${item.slug}`} style={{ textDecoration: "none" }}>
                        <Button label="Anchor" icon="pi pi-link" outlined severity="secondary" />
                      </a>
                      <Button label="Copy Link" icon="pi pi-copy" outlined severity="secondary" onClick={() => void copyReleaseLink(item.slug)} />
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {(item.tags || []).map((tag) => (
                      <Tag key={`${item.id}-${tag}`} value={tag} severity="warning" />
                    ))}
                  </div>

                  {(item.sections || []).map((section) => (
                    <div key={`${item.id}-${section.title}`} style={{ marginTop: "1.25rem" }}>
                      <h4 style={{ color: "#0f3d63", marginBottom: "0.55rem" }}>{section.title}</h4>
                      <ul>
                        {(section.items || []).map((detail) => (
                          <li key={detail}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotes;
